<template>
  <div>
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="mx-auto flex items-center justify-center h-20"
    >
      <transition name="fade">
        <Icon type="loading-spinner" />
      </transition>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="mb-6 mt-5 bg-red-50 border border-red-200 rounded-lg p-4"
    >
      <div class="flex items-start space-x-4">
        <div class="flex-shrink-0">
          <Icon
            type="exclamation-triangle"
            class="h-8 w-8 text-red-600"
          />
        </div>
        <div>
          <h3 class="text-lg font-semibold text-red-800 mb-2">
            Error Loading Domain Info
          </h3>
          <p class="text-red-700">
            Unable to load custom domain information. Please try again later.
          </p>
        </div>
      </div>
    </div>

    <!-- Trial/Starter Plans -->
    <div
      v-else-if="isTrialOrStarter"
      class="mb-6 mt-5 rounded-lg bg-muted p-4"
    >
      <h4 class="mb-2 font-medium">
        Need to use a custom domain?
      </h4>
      <p class="mb-3">
        Upgrade to enable custom domain features for your account.
      </p>
      <div class="flex items-center">
        <a
          class="button save bg-gradient-primary rounded-lg border-0 p-3 text-base leading-4 font-medium"
          target="_blank"
          rel="noreferrer"
          :href="constants.DASHBOARD_PLANS_URL"
        >
          View Plans and Upgrade
        </a>
        <a
          class="text-subtle hover:text-default underline ml-4 flex items-center"
          target="_blank"
          rel="noreferrer"
          :href="constants.LEARN_MORE_URL"
        >
          <span>Learn More</span>
          <Icon
            type="arrow-top-right-thick"
            class="h-4 w-4 ml-2 top-1"
          />
        </a>
      </div>
    </div>

    <!-- Pro+ Plans without Custom Domain -->
    <div
      v-else-if="!hasCustomDomains"
      class="mb-6 mt-5 rounded-lg bg-muted p-4"
    >
      <h4 class="mb-2 font-medium">
        Need to use a custom domain?
      </h4>
      <p class="mb-3">
        Custom domains are included with your plan.
        Submit a request and our Support team will contact you to assist with the setup.
      </p>
      <a
        class="button save bg-gradient-primary rounded-lg border-0 p-3 text-base leading-4 font-medium"
        target="_blank"
        rel="noreferrer"
        :href="constants.REQUEST_URL"
      >
        Request Custom Domain
      </a>
    </div>

    <!-- Pro+ Plans with Custom Domain -->
    <div
      v-else-if="hasCustomDomains"
      class="mb-6 mt-5 bg-warning border text-warning-emphasis rounded-lg p-4"
    >
      <h4 class="mb-2 font-medium text-warning-emphasis">
        You're using a custom domain
      </h4>
      <p class="mb-3">
        Updating your app URL may require updates to your DNS entries in your custom domain.
      </p>
      <router-link
        class="font-medium text-warning-emphasis hover:text-warning-900 hover:underline"
        to="/settings/custom-domain"
      >
        Custom Domain Settings
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '@/components/ui/Icon.vue';
import CustomDomainUtils from '@/components/settings/custom-domains/CustomDomainUtils';

export default {
  name: 'CustomDomainAppSettingsMessage',
  components: {
    Icon,
  },
  mixins: [
    CustomDomainUtils,
  ],
  computed: {
    ...mapGetters([
      'app',
    ]),
    planLevel() {
      return this.app?.account?.product_plan?.level || 0;
    },
    isTrialOrStarter() {
      return this.app?.account?.is_trial || this.planLevel < 2;
    },
  },
  async mounted() {
    // Only fetch custom domains if not on trial/starter plan
    if (this.isTrialOrStarter) {
      this.isLoading = false;
    } else {
      await this.fetchCustomDomains();
    }
  },
};
</script>

<style scoped>
.bg-muted {
  background-color: #f8f9fa;
}

.text-subtle {
  color: #6b7280;
}

.text-emphasis {
  color: #374151;
}

.text-default {
  color: #1f2937;
}

.font-medium {
  font-weight: 500;
}
</style>
